// Use explicit imports to avoid SWC transformation issues
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@/components/theme-provider';

// Error boundary for the entire application
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; errorMessage: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('Application error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-100 text-red-800 rounded">
          <h2 className="text-xl font-bold">Something went wrong</h2>
          <p>The application encountered an error: {this.state.errorMessage}</p>
          <button
            className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Safely import TempoDevtools with error handling
let TempoDevtools: { init: () => void } = { init: () => {} };
try {
  // Use dynamic import to avoid SWC transformation issues
  import('tempo-devtools')
    .then((module) => {
      TempoDevtools = module.TempoDevtools;
      // Wrap initialization in try/catch to prevent crashes
      try {
        TempoDevtools.init();
      } catch (e) {
        console.warn('Failed to initialize TempoDevtools:', e);
      }
    })
    .catch((err) => {
      console.warn('Failed to import TempoDevtools:', err);
    });
} catch (e) {
  console.warn('Failed to load TempoDevtools:', e);
}

// Detect Capacitor environment and add class to html for CSS safe area handling
if ((window as any).Capacitor) {
  document.documentElement.classList.add('capacitor');
  const platform = (window as any).Capacitor.getPlatform?.();
  if (platform === 'android') {
    document.documentElement.classList.add('capacitor-android');
    // Set dark status bar icons (for white backgrounds) and fully transparent background
    import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
      StatusBar.setOverlaysWebView({ overlay: true });
      StatusBar.setStyle({ style: Style.Light });
      StatusBar.setBackgroundColor({ color: '#00000000' });
    }).catch(() => {});
  }
}

// BASE_URL may be './' for Capacitor builds — normalize to '/' for BrowserRouter
const rawBase = import.meta.env.BASE_URL;
const basename = rawBase === './' ? '/' : rawBase;

// Safely get the root element
const rootElement = document.getElementById('root');

// Only render if the root element exists
if (rootElement) {
  try {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <ErrorBoundary>
          <ThemeProvider defaultTheme="light">
            <BrowserRouter basename={basename}>
              <App />
            </BrowserRouter>
          </ThemeProvider>
        </ErrorBoundary>
      </React.StrictMode>,
    );
  } catch (error) {
    console.error('Failed to render application:', error);
    // Fallback rendering in case of error
    rootElement.innerHTML = `
      <div style="padding: 20px; font-family: sans-serif;">
        <h2 style="color: #e53e3e;">Application Failed to Start</h2>
        <p>There was a problem initializing the application. Please check the console for more details.</p>
        <button style="margin-top: 10px; padding: 8px 16px; background-color: #3182ce; color: white; border: none; border-radius: 4px; cursor: pointer;" onclick="window.location.reload()">
          Refresh Page
        </button>
      </div>
    `;
  }
} else {
  console.error('Root element not found');
}
